import classNames from 'classnames'
import { useCallback, useMemo, useState } from 'react'
import { match } from 'ts-pattern'
import { pluralize } from '../../utils/text-utils.ts'
import * as exposureStyles from './exposure-tone.css.ts'
import * as styles from './threat-coverage-graph.css.ts'
import {
  buildCoverageModel,
  type CoverageControl,
  type CoverageModel,
  type CoverageThreat,
  type ThreatModelCoverageInput,
  type ThreatRelevance,
} from './threat-coverage.ts'
import { coerceInternetFacing } from './threat-model-report.tsx'

/**
 * A deterministic control→threat coverage graph.
 *
 * Threats come from each finding's `vulnClass`, coverage from each control's own `mitigates` list;
 * a present/likely threat that no control claims to mitigate is drawn as a *coverage gap*. The join is
 * the fixed taxonomy in `threat-coverage.ts` — no prose is read to decide edges. See that module for
 * why this whole join is deleted once the API exposes structured `mitigatedBy` references.
 */

// `top` is where the first card sits; the column heading sits at y=58, so this is the gap beneath it.
const CANVAS = { width: 1180, height: 760, top: 84, bottom: 740, nodeWidth: 250, leftPad: 30 } as const
const NODE_MAX_HEIGHT = 46
const NODE_CORNER_RADIUS = 7
const GAP_BADGE_WIDTH = 34
/** Rough advance width of the label font at its rendered size, for truncation only. */
const APPROX_CHAR_WIDTH = 6.2

type NodeBox = { x: number; y: number; width: number; height: number; centerY: number }
type RelevanceVariant = 'present' | 'likely' | 'unlikely'

const relevanceVariant = (relevance: ThreatRelevance): RelevanceVariant =>
  match(relevance)
    .returnType<RelevanceVariant>()
    .with('present', () => 'present')
    .with('likely', () => 'likely')
    .otherwise(() => 'unlikely')

const truncate = ({ text, availableWidth }: { text: string; availableWidth: number }): string => {
  const maxCharacters = Math.floor(availableWidth / APPROX_CHAR_WIDTH)
  if (text.length <= maxCharacters) {
    return text
  }
  return `${text.slice(0, Math.max(1, maxCharacters - 1)).trimEnd()}…`
}

const placeColumn = ({ ids, x }: { ids: string[]; x: number }): Record<string, NodeBox> => {
  const slot = (CANVAS.bottom - CANVAS.top) / Math.max(ids.length, 1)
  const height = Math.min(NODE_MAX_HEIGHT, slot - 10)
  return Object.fromEntries(
    ids.map((id, index) => {
      const y = CANVAS.top + index * slot + (slot - height) / 2
      return [id, { x, width: CANVAS.nodeWidth, y, height, centerY: y + height / 2 }]
    })
  )
}

const computeLayout = (model: CoverageModel): Record<string, NodeBox> => ({
  ...placeColumn({ ids: model.controls.map(control => control.id), x: CANVAS.leftPad }),
  ...placeColumn({ ids: model.threats.map(threat => threat.id), x: CANVAS.width - CANVAS.leftPad - CANVAS.nodeWidth }),
})

const edgeId = ({ controlId, threatId }: { controlId: string; threatId: string }) => `${controlId}__${threatId}`

const edgePath = ({ from, to }: { from: NodeBox; to: NodeBox }): string => {
  const startX = from.x + from.width
  const endX = to.x
  const midX = (startX + endX) / 2
  return `M${startX},${from.centerY} C${midX},${from.centerY} ${midX},${to.centerY} ${endX},${to.centerY}`
}

type Highlight = { nodeIds: Set<string>; edgeIds: Set<string> }
const EMPTY_HIGHLIGHT: Highlight = { nodeIds: new Set(), edgeIds: new Set() }

const computeHighlight = ({
  model,
  selectedId,
}: {
  model: CoverageModel
  selectedId: string | undefined
}): Highlight => {
  if (!selectedId) {
    return EMPTY_HIGHLIGHT
  }
  const nodeIds = new Set<string>([selectedId])
  const edgeIds = new Set<string>()
  const control = model.controls.find(candidate => candidate.id === selectedId)
  if (control) {
    for (const threatId of control.coveredThreatIds) {
      nodeIds.add(threatId)
      edgeIds.add(edgeId({ controlId: control.id, threatId }))
    }
    return { nodeIds, edgeIds }
  }
  const threat = model.threats.find(candidate => candidate.id === selectedId)
  if (threat) {
    for (const controlId of threat.mitigatingControlIds) {
      nodeIds.add(controlId)
      edgeIds.add(edgeId({ controlId, threatId: threat.id }))
    }
  }
  return { nodeIds, edgeIds }
}

type CoverageNodeProps = {
  box: NodeBox
  label: string
  fillVariant: keyof typeof styles.nodeFill
  strokeVariant: keyof typeof styles.nodeStroke
  kind?: string
  isGap: boolean
  isDimmed: boolean
  isPinned: boolean
  onEnter: () => unknown
  onLeave: () => unknown
  onActivate: () => unknown
}

const CoverageNode: React.FC<CoverageNodeProps> = ({
  box,
  label,
  fillVariant,
  strokeVariant,
  kind,
  isGap,
  isDimmed,
  isPinned,
  onEnter,
  onLeave,
  onActivate,
}) => {
  const labelWidth = box.width - 24 - (isGap ? GAP_BADGE_WIDTH + 8 : 0)
  return (
    <g
      className={classNames(styles.nodeGroup, { [styles.dimmed]: isDimmed })}
      role="button"
      tabIndex={0}
      aria-label={label}
      aria-pressed={isPinned}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onFocus={onEnter}
      onBlur={onLeave}
      onClick={event => {
        // Without this the canvas below would clear the selection this click just made.
        event.stopPropagation()
        onActivate()
      }}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onActivate()
        }
      }}
    >
      <rect
        x={box.x}
        y={box.y}
        width={box.width}
        height={box.height}
        rx={NODE_CORNER_RADIUS}
        className={classNames(styles.nodeRect, styles.nodeFill[fillVariant], styles.nodeStroke[strokeVariant])}
      />
      {kind ? (
        <text className={styles.kindLabel} x={box.x + 12} y={box.y + 15}>
          {kind.replace(/_/g, ' ')}
        </text>
      ) : undefined}
      <text className={styles.nodeLabel} x={box.x + 12} y={kind ? box.y + 31 : box.centerY + 4}>
        {truncate({ text: label, availableWidth: labelWidth })}
      </text>
      {isGap ? (
        <>
          <rect
            className={styles.gapBadge}
            x={box.x + box.width - GAP_BADGE_WIDTH - 6}
            y={box.centerY - 8}
            width={GAP_BADGE_WIDTH}
            height={15}
            rx={4}
          />
          <text
            className={styles.gapBadgeText}
            x={box.x + box.width - GAP_BADGE_WIDTH / 2 - 6}
            y={box.centerY + 3}
            textAnchor="middle"
          >
            GAP
          </text>
        </>
      ) : undefined}
      <title>{label}</title>
    </g>
  )
}

export type ThreatCoverageGraphProps = ThreatModelCoverageInput

export const ThreatCoverageGraph: React.FC<ThreatCoverageGraphProps> = ({
  internetFacing,
  controls,
  vulnerabilities,
}) => {
  const model = useMemo(() => buildCoverageModel({ controls, vulnerabilities }), [controls, vulnerabilities])
  const layout = useMemo(() => computeLayout(model), [model])

  const [hoveredId, setHoveredId] = useState<string>()
  const [pinnedId, setPinnedId] = useState<string>()
  const selectedId = pinnedId ?? hoveredId
  const highlight = useMemo(() => computeHighlight({ model, selectedId }), [model, selectedId])

  const handleEnter = useCallback((id: string) => {
    setHoveredId(current => (current === id ? current : id))
  }, [])
  const handleLeave = useCallback(() => setHoveredId(undefined), [])
  const handleActivate = useCallback((id: string) => {
    setPinnedId(current => (current === id ? undefined : id))
  }, [])

  const gapCount = model.threats.filter(threat => threat.isGap).length
  const isSelectionActive = selectedId !== undefined
  const nodeProps = (id: string) => ({
    isDimmed: isSelectionActive && !highlight.nodeIds.has(id),
    isPinned: pinnedId === id,
    onEnter: () => handleEnter(id),
    onLeave: handleLeave,
    onActivate: () => handleActivate(id),
  })

  return (
    <div className={styles.container}>
      <div className={styles.stage}>
        <div className={styles.canvasCard}>
          {/* Counts, provenance and badges ride a header inside the graph zone, the way the trust-zone
              view's control bar does — three stacked rows above the card left the graph below the fold. */}
          <div className={styles.canvasHeader}>
            <div className={styles.canvasHeaderRow}>
              <div className={styles.chips}>
                {internetFacing ? (
                  <span
                    className={exposureStyles.exposureChip({ internetFacing: coerceInternetFacing(internetFacing) })}
                  >
                    Internet-facing <span className={styles.chipStrong}>{internetFacing}</span>
                  </span>
                ) : undefined}
                {/* No "N of M covered": it is the arithmetic complement of the gap count, so the two chips
                    said the same thing twice and the gap is the one worth acting on. */}
                <span className={classNames(styles.chip, styles.chipAlarm)}>
                  <span className={styles.chipAlarmValue}>{gapCount}</span> coverage{' '}
                  {pluralize(gapCount, 'gap', 'gaps')}
                </span>
              </div>
              <CoverageLegend />
            </div>
            {/* The counts moved onto the two column headings inside the graph, where the things being
                counted actually are; repeating them here was the same fact three times on one screen. */}
            <div className={styles.hint}>
              Every node and edge is computed from the threat model alone — threats from each finding, coverage from
              each control&rsquo;s own mitigates list. Hover to trace coverage; select for detail.
            </div>
          </div>

          {/* Clicking away from every node clears the pin, so the canvas returns to full opacity instead
              of stranding the reader in a dimmed view with no way out — as the trust-zone view does. */}
          <div className={styles.canvasScroll} data-testid="coverage-canvas" onClick={() => setPinnedId(undefined)}>
            <svg
              className={styles.svg}
              viewBox={`0 0 ${CANVAS.width} ${CANVAS.height}`}
              role="group"
              aria-label="Security controls on the left connected to the threats they mitigate on the right"
            >
              <g>
                {model.controls.flatMap(control =>
                  control.coveredThreatIds.map(threatId => {
                    const from = layout[control.id]
                    const to = layout[threatId]
                    if (!from || !to) {
                      return undefined
                    }
                    const id = edgeId({ controlId: control.id, threatId })
                    const isActive = highlight.edgeIds.has(id)
                    return (
                      <path
                        key={id}
                        d={edgePath({ from, to })}
                        className={classNames(styles.edge, {
                          [styles.edgeActive]: isActive,
                          [styles.edgeDimmed]: isSelectionActive && !isActive,
                        })}
                      />
                    )
                  })
                )}
              </g>

              <g>
                {model.controls.map(control => {
                  const box = layout[control.id]
                  return box ? (
                    <CoverageNode
                      key={control.id}
                      box={box}
                      label={control.name}
                      kind={control.kind}
                      fillVariant="control"
                      strokeVariant="control"
                      isGap={false}
                      {...nodeProps(control.id)}
                    />
                  ) : undefined
                })}
                {model.threats.map(threat => {
                  const box = layout[threat.id]
                  if (!box) {
                    return undefined
                  }
                  const variant = relevanceVariant(threat.relevance)
                  return (
                    <CoverageNode
                      key={threat.id}
                      box={box}
                      label={threat.name}
                      fillVariant={variant}
                      strokeVariant={variant}
                      isGap={threat.isGap}
                      {...nodeProps(threat.id)}
                    />
                  )
                })}
              </g>

              <ColumnHeaders model={model} layout={layout} />
            </svg>
          </div>
        </div>

        <ThreatCoverageDetail model={model} selectedId={selectedId} />
      </div>

      <div className={styles.method}>
        <span className={styles.methodStrong}>How this is built: </span>
        threats come from each finding&rsquo;s vulnerability class; a threat is covered when some control&rsquo;s
        mitigates list names it. Both are normalized into a fixed taxonomy by keyword rules. A coverage gap is a present
        or likely threat that no control claims to mitigate — deterministic, with no model in the loop.
      </div>
    </div>
  )
}

const ColumnHeaders: React.FC<{ model: CoverageModel; layout: Record<string, NodeBox> }> = ({ model, layout }) => {
  const controlBox = model.controls.length > 0 ? layout[model.controls[0]?.id ?? ''] : undefined
  const threatBox = model.threats.length > 0 ? layout[model.threats[0]?.id ?? ''] : undefined
  return (
    <g>
      {controlBox ? (
        <>
          {/* One line, not a title over a count — the two said the same thing in two registers. */}
          <text className={styles.columnTitle} x={controlBox.x} y={58}>
            {model.controls.length} security {pluralize(model.controls.length, 'control', 'controls')}
          </text>
        </>
      ) : undefined}
      {threatBox ? (
        <>
          <text className={styles.columnTitle} x={threatBox.x} y={58}>
            {model.threats.length} {pluralize(model.threats.length, 'threat', 'threats')}
          </text>
        </>
      ) : undefined}
    </g>
  )
}

const CoverageLegend: React.FC = () => (
  <div className={styles.legend}>
    <span className={styles.legendItem}>
      <span className={classNames(styles.legendSwatch, styles.legendSwatchColor.control)} />
      Control
    </span>
    <span className={styles.legendSeparator} />
    {(['present', 'likely', 'unlikely'] as const).map(variant => (
      <span key={variant} className={styles.legendItem}>
        <span className={classNames(styles.legendSwatch, styles.legendSwatchColor[variant])} />
        {variant.charAt(0).toUpperCase() + variant.slice(1)}
      </span>
    ))}
    <span className={styles.legendSeparator} />
    <span className={styles.legendItem}>
      <span className={classNames(styles.legendSwatch, styles.legendSwatchColor.gap)} />
      Coverage gap
    </span>
  </div>
)

type ThreatCoverageDetailProps = { model: CoverageModel; selectedId: string | undefined }

const ThreatCoverageDetail: React.FC<ThreatCoverageDetailProps> = ({ model, selectedId }) => {
  const control = model.controls.find(candidate => candidate.id === selectedId)
  const threat = model.threats.find(candidate => candidate.id === selectedId)

  if (control) {
    return <ControlDetail model={model} control={control} />
  }
  if (threat) {
    return <ThreatDetail model={model} threat={threat} />
  }
  return (
    <aside className={styles.detail}>
      <div className={styles.eyebrow}>Detail</div>
      <p className={styles.detailEmpty}>
        Select a threat or control. A threat is a coverage gap when no control&rsquo;s mitigates list names it — read
        straight from the data, so the gap is a fact about the payload, not a guess.
      </p>
    </aside>
  )
}

const ControlDetail: React.FC<{ model: CoverageModel; control: CoverageControl }> = ({ model, control }) => {
  const coveredNames = control.coveredThreatIds
    .map(threatId => model.threats.find(threat => threat.id === threatId)?.name)
    .filter((name): name is string => name !== undefined)
  return (
    <aside className={styles.detail}>
      <div className={styles.eyebrow}>Security control</div>
      <h4 className={styles.detailTitle}>{control.name}</h4>
      <span className={classNames(styles.badgeBase, styles.badge.control)}>{control.kind.replace(/_/g, ' ')}</span>
      <div className={styles.field}>
        <span className={styles.fieldKey}>Declares it mitigates</span>
        {control.mitigates.length > 0 ? (
          <div className={styles.tagList}>
            {control.mitigates.map(tag => (
              <span key={tag} className={styles.tag}>
                {tag}
              </span>
            ))}
          </div>
        ) : (
          <span className={styles.fieldValue}>—</span>
        )}
      </div>
      <div className={styles.field}>
        <span className={styles.fieldKey}>Covers threats</span>
        <span className={styles.fieldValue}>
          {coveredNames.length > 0 ? coveredNames.join(' · ') : '— (no listed threat matched)'}
        </span>
      </div>
      {control.scope ? (
        <div className={styles.field}>
          <span className={styles.fieldKey}>Scope</span>
          <span className={styles.fieldValue}>{control.scope}</span>
        </div>
      ) : undefined}
    </aside>
  )
}

const ThreatDetail: React.FC<{ model: CoverageModel; threat: CoverageThreat }> = ({ model, threat }) => {
  const variant = relevanceVariant(threat.relevance)
  const mitigatingNames = threat.mitigatingControlIds
    .map(controlId => model.controls.find(control => control.id === controlId)?.name)
    .filter((name): name is string => name !== undefined)
  return (
    <aside className={styles.detail}>
      <div className={styles.eyebrow}>Threat</div>
      <h4 className={styles.detailTitle}>{threat.name}</h4>
      <span className={classNames(styles.badgeBase, styles.badge[variant])}>Relevance · {threat.relevance}</span>
      <div className={styles.field}>
        <span className={styles.fieldKey}>Mapped from</span>
        <div className={styles.tagList}>
          {threat.sources.map(source => (
            <span key={source} className={styles.tag}>
              {source}
            </span>
          ))}
        </div>
      </div>
      {threat.isGap ? (
        <div className={styles.gapNote}>
          <span aria-hidden>⚠</span>
          <span>
            <span className={styles.gapNoteStrong}>Coverage gap. </span>
            No control&rsquo;s mitigates list names this threat.
          </span>
        </div>
      ) : (
        <div className={styles.field}>
          <span className={styles.fieldKey}>Mitigated by</span>
          <ul className={styles.controlList}>
            {mitigatingNames.map(name => (
              <li key={name} className={styles.controlListItem}>
                {name}
              </li>
            ))}
          </ul>
        </div>
      )}
      {threat.reachability ? (
        <div className={styles.field}>
          <span className={styles.fieldKey}>Reachability</span>
          <span className={styles.fieldValue}>{threat.reachability}</span>
        </div>
      ) : undefined}
      {threat.blastRadius ? (
        <div className={styles.field}>
          <span className={styles.fieldKey}>Blast radius</span>
          <span className={styles.fieldValue}>{threat.blastRadius}</span>
        </div>
      ) : undefined}
    </aside>
  )
}
